<template>
    <MultiSelect
        ref="multiselect"
        :model-value="draft"
        :options="panelOptions"
        :option-label="optionLabel"
        :data-key="dataKey"
        :option-group-label="isGrouped ? 'label' : undefined"
        :option-group-children="isGrouped ? 'items' : undefined"
        :placeholder="placeholder"
        :filter-placeholder="searchPlaceholder"
        :empty-message="loading ? loadingText : emptyText"
        :empty-filter-message="loading ? loadingText : emptyFilterText"
        :select-all="allVisibleSelected"
        :show-toggle-all="visibleOptions.length > 0"
        :max-selected-labels="null"
        display="chip"
        filter
        reset-filter-on-hide
        fluid
        :overlay-style="overlayStyle"
        class="e-multiselect"
        @update:model-value="onDraftChange"
        @selectall-change="onSelectAllChange"
        @filter="onFilter"
        @before-show="onBeforeShow"
        @before-hide="onBeforeHide"
    >
        <template v-if="loading" #dropdownicon>
            <i class="pi pi-spin pi-spinner" aria-hidden="true" />
        </template>

        <template #chip="{ value, removeCallback }">
            <Chip
                :label="labelOf(value)"
                :title="labelOf(value)"
                removable
                @remove="event => removeChip(event, removeCallback)"
            />
        </template>

        <template #option="{ option }">
            <span class="tw:min-w-0 tw:flex-1 tw:truncate" :title="labelOf(option)" data-test="e-multiselect-option-label">
                {{ labelOf(option) }}
            </span>
        </template>

        <template v-if="headerText" #header>
            <div
                class="tw:px-3 tw:pt-2 tw:pb-1 tw:text-sm tw:text-surface-600 tw:dark:text-surface-300"
                data-test="e-multiselect-header-text"
            >
                {{ headerText }}
            </div>
        </template>

        <template v-if="isGrouped" #optiongroup="{ option: group }">
            <div class="tw:flex tw:min-w-0 tw:flex-col tw:gap-1">
                <button
                    v-if="group.collapsible"
                    type="button"
                    class="tw:flex tw:w-full tw:min-w-0 tw:items-center tw:gap-1 tw:text-left tw:font-semibold tw:cursor-pointer"
                    :aria-expanded="group.isOpen ? 'true' : 'false'"
                    data-test="e-multiselect-group-toggle"
                    @click.stop="toggleGroup(group.label)"
                    @keydown.enter.prevent.stop="toggleGroup(group.label)"
                    @keydown.space.prevent.stop="toggleGroup(group.label)"
                >
                    <i :class="group.isOpen ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" aria-hidden="true" />
                    <span class="tw:min-w-0 tw:truncate" :title="group.label" data-test="e-multiselect-group-label">
                        {{ group.label }}
                    </span>
                    <span class="tw:shrink-0 tw:font-normal tw:text-surface-600 tw:dark:text-surface-300" data-test="e-multiselect-group-count">{{ ` (${group.total})` }}</span>
                </button>
                <span v-else class="tw:min-w-0 tw:truncate tw:font-semibold" :title="group.label" data-test="e-multiselect-group-label">
                    {{ group.label }}
                </span>
                <span
                    v-if="group.emptyText"
                    class="tw:text-sm tw:font-normal tw:italic tw:opacity-75"
                    data-test="e-multiselect-group-empty"
                >
                    {{ group.emptyText }}
                </span>
            </div>
        </template>

        <template v-if="applyButton" #footer>
            <div class="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:px-3 tw:py-2 tw:border-t tw:border-surface-200 tw:dark:border-surface-700">
                <span class="tw:text-sm tw:opacity-75" data-test="e-multiselect-selected-count">
                    {{ draft.length }} selected
                </span>
                <Button label="Apply" size="small" data-test="e-multiselect-apply" @click="applyAndClose" />
            </div>
        </template>
    </MultiSelect>
</template>

<script setup>
/**
 * EMultiselect - generic multi-select dropdown built on PrimeVue MultiSelect.
 *
 * Renders the selection as chips that wrap onto new lines (the box grows, nothing is
 * summarized as "+N"), a search box and a select-all checkbox in the panel. It knows
 * nothing about the data it shows; callers pass the options and all texts.
 *
 * Behavior that differs from a bare PrimeVue MultiSelect:
 *   - `loading` is never passed to PrimeVue (it refuses to open while loading). The panel
 *     opens on every click and shows `loadingText` until the options arrive.
 *   - Select-all ticks or unticks only the options currently visible (search result,
 *     open groups); hidden selections are kept. It is hidden while no option is visible.
 *   - Chip labels are read from the selected values, so they never depend on which
 *     options are rendered (loading, collapsed groups).
 *   - The open panel is as wide as the box (never wider than the viewport minus a
 *     gutter); long option and group labels truncate to one line with the full label as
 *     a hover title, so the footer stays on screen.
 *   - With `apply-button`, ticks stay pending while the panel is open. Apply, or any other
 *     close (Escape, click outside, chevron), commits them once, only if they changed.
 *     Removing a chip from the closed box commits straight away. Without it, every tick
 *     updates `v-model` immediately.
 *
 * Grouped mode: pass `groups` instead of `options`:
 *   [{ label, items, collapsible?, emptyText? }]
 *   - `label` names the group header and must be unique; `items` are option objects.
 *   - `emptyText` shows under the header of a group without items (no checkbox).
 *   - Any number of groups may be `collapsible`. They start collapsed every time the panel
 *     opens, show their item count, toggle on header click or Enter/Space, open on their
 *     own while the search matches an item inside them and collapse again when it clears.
 *   - Search keeps only the headers of groups with matches.
 *
 * USAGE EXAMPLES:
 *
 *   <e-multiselect
 *     v-model="selectedPrograms"
 *     :options="programs"
 *     :loading="loadingPrograms"
 *     placeholder="All programs"
 *     search-placeholder="Search programs"
 *     loading-text="Loading programs…"
 *     empty-text="No programs found"
 *     empty-filter-text="No programs match"
 *     apply-button
 *     @apply="reload"
 *   />
 *
 *   <e-multiselect
 *     v-model="selectedCohorts"
 *     :groups="[
 *       { label: 'AVN', items: avnCohorts },
 *       { label: 'AVN PILOT', items: [], emptyText: 'No cohorts are linked to AVN PILOT yet.' },
 *       { label: 'Not linked to a program', items: unlinkedCohorts, collapsible: true },
 *     ]"
 *     header-text="6 cohorts in AVN, MSOT"
 *     apply-button
 *   />
 *
 * Other attributes (input-id, append-to, data-test, …) fall through to PrimeVue MultiSelect.
 */
import Button from 'primevue/button';
import Chip from 'primevue/chip';
import MultiSelect from 'primevue/multiselect';
import { computed, ref, watch } from 'vue';

defineOptions({
    name: 'EMultiselect',
});

const props = defineProps({
    /** Selected option objects. */
    modelValue: {
        type: Array,
        default: () => [],
    },
    /** Option objects (flat mode). Ignored when `groups` is set. */
    options: {
        type: Array,
        default: () => [],
    },
    /** Groups `[{ label, items, collapsible?, emptyText? }]`; switches on grouped mode. */
    groups: {
        type: Array,
        default: null,
    },
    /** Field shown as the option and chip label. */
    optionLabel: {
        type: String,
        default: 'name',
    },
    /** Field that identifies an option. */
    dataKey: {
        type: String,
        default: 'id',
    },
    /** While true the panel shows `loadingText` and the chevron a spinner. */
    loading: {
        type: Boolean,
        default: false,
    },
    /** Box text when nothing is selected. */
    placeholder: {
        type: String,
        default: '',
    },
    searchPlaceholder: {
        type: String,
        default: 'Search',
    },
    loadingText: {
        type: String,
        default: 'Loading…',
    },
    /** Panel text when loading finished and there are no options. */
    emptyText: {
        type: String,
        default: 'No options found',
    },
    /** Panel text when the search matches nothing. */
    emptyFilterText: {
        type: String,
        default: 'No options match',
    },
    /** Optional line at the top of the open panel. */
    headerText: {
        type: String,
        default: '',
    },
    /** Shows the "N selected" + Apply footer and commits only on Apply or close. */
    applyButton: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue', 'apply']);

const multiselect = ref(null);
const isPanelOpen = ref(false);
const query = ref('');
const expandedGroups = ref(new Set());
/** Width of the closed box, measured when the panel opens. */
const boxWidth = ref(0);

/** Viewport room left beside the panel so it never touches the screen edges. */
const OVERLAY_VIEWPORT_GUTTER = '2rem';

/**
 * PrimeVue sets the panel's min-width to the box width; capping max-width at the same
 * width keeps long labels from stretching the panel (they truncate instead).
 */
const overlayStyle = computed(() => {
    const viewportCap = `calc(100vw - ${OVERLAY_VIEWPORT_GUTTER})`;

    return { maxWidth: boxWidth.value > 0 ? `min(${boxWidth.value}px, ${viewportCap})` : viewportCap };
});

/** Ticks shown in the panel; equals `committed` whenever the panel is closed. */
const draft = ref([...props.modelValue]);
/** Last value received from or sent to the parent. */
let committed = [...props.modelValue];

const isGrouped = computed(() => Array.isArray(props.groups));

const keyOf = option => option?.[props.dataKey];
const labelOf = option => String(option?.[props.optionLabel] ?? '');
const keySet = list => new Set(list.map(keyOf));
const haveSameKeys = (first, second) => {
    const firstKeys = keySet(first);
    return first.length === second.length && second.every(option => firstKeys.has(keyOf(option)));
};

/** Same matching as PrimeVue's "contains" filter: case- and accent-insensitive. */
const normalize = text => String(text).normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase();
const normalizedQuery = computed(() => normalize(query.value.trim()));
const matchesQuery = option => !normalizedQuery.value || normalize(labelOf(option)).includes(normalizedQuery.value);

/** Groups as PrimeVue renders them: collapsed groups carry no items. */
const panelGroups = computed(() => {
    if (!isGrouped.value || props.loading) {
        return [];
    }

    return props.groups.map(group => {
        const items = group.items ?? [];
        const isOpen =
            !group.collapsible ||
            expandedGroups.value.has(group.label) ||
            (Boolean(normalizedQuery.value) && items.some(matchesQuery));

        return {
            label: group.label,
            items: isOpen ? items : [],
            collapsible: Boolean(group.collapsible),
            isOpen,
            total: items.length,
            emptyText: items.length === 0 ? (group.emptyText ?? '') : '',
        };
    });
});

const panelOptions = computed(() => {
    if (isGrouped.value) {
        return panelGroups.value;
    }

    return props.loading ? [] : props.options;
});

/** Options the user can currently see (and select-all acts on). */
const visibleOptions = computed(() => {
    const options = isGrouped.value ? panelGroups.value.flatMap(group => group.items) : panelOptions.value;

    return options.filter(option => !option?.disabled && matchesQuery(option));
});

const allVisibleSelected = computed(() => {
    const draftKeys = keySet(draft.value);
    return visibleOptions.value.length > 0 && visibleOptions.value.every(option => draftKeys.has(keyOf(option)));
});

const commit = () => {
    if (haveSameKeys(draft.value, committed)) {
        return;
    }

    committed = [...draft.value];
    emit('update:modelValue', [...committed]);

    if (props.applyButton) {
        emit('apply', [...committed]);
    }
};

const onDraftChange = value => {
    draft.value = value;

    if (!props.applyButton || !isPanelOpen.value) {
        commit();
    }
};

const onSelectAllChange = ({ checked }) => {
    const visibleKeys = keySet(visibleOptions.value);
    const kept = draft.value.filter(option => !visibleKeys.has(keyOf(option)));

    onDraftChange(checked ? [...kept, ...visibleOptions.value] : kept);
};

const removeChip = (event, removeCallback) => {
    event?.stopPropagation();
    removeCallback(event);
};

const onFilter = ({ value }) => {
    query.value = value ?? '';
};

const toggleGroup = label => {
    const expanded = new Set(expandedGroups.value);

    if (expanded.has(label)) {
        expanded.delete(label);
    } else {
        expanded.add(label);
    }

    expandedGroups.value = expanded;
};

const onBeforeShow = () => {
    isPanelOpen.value = true;
    boxWidth.value = multiselect.value?.$el?.getBoundingClientRect().width ?? 0;
    query.value = '';
    expandedGroups.value = new Set();
};

const onBeforeHide = () => {
    isPanelOpen.value = false;
    query.value = '';

    if (props.applyButton) {
        commit();
    }
};

const applyAndClose = () => {
    commit();
    multiselect.value?.hide(true);
};

/**
 * Follow the parent's value. While the panel is open, the user's pending ticks are
 * re-applied on top of it, so a selection that resolves late (options arriving while
 * open) is neither wiped nor duplicated on close.
 */
watch(
    () => props.modelValue,
    value => {
        const draftKeys = keySet(draft.value);
        const committedKeys = keySet(committed);
        const valueKeys = keySet(value);
        const added = draft.value.filter(option => !committedKeys.has(keyOf(option)));
        const removedKeys = new Set(committed.filter(option => !draftKeys.has(keyOf(option))).map(keyOf));

        committed = [...value];
        draft.value = [
            ...value.filter(option => !removedKeys.has(keyOf(option))),
            ...added.filter(option => !valueKeys.has(keyOf(option))),
        ];
    }
);
</script>

<style scoped lang="scss">
// The closed box grows so every selected chip shows, wrapping onto new lines.
.e-multiselect {
    height: auto;
    align-items: flex-start;

    // min-width: 0 lets the label area shrink below its widest chip, so the chip
    // ellipsizes instead of pushing the dropdown chevron out of the box.
    :deep(.p-multiselect-label-container) {
        overflow: visible;
        min-width: 0;
    }

    :deep(.p-multiselect-label) {
        flex-wrap: wrap;
        white-space: normal;
        overflow: visible;
    }

    :deep(.p-multiselect-dropdown) {
        align-self: stretch;
    }

    :deep(.p-multiselect-chip-item),
    :deep(.p-chip) {
        max-width: 100%;
        min-width: 0;
    }

    :deep(.p-chip-label) {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
</style>
